import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DisclosureHost } from "../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { BatchResolveForm } from "../../../../pages/app/provider/collabration/tasks/BatchResolveDrawer";
import { usePatientResolveTask } from "../../../../queries/collaboration/tasks/use-batch-resolve";
import { getXtenantId, showToastOnApiCall } from "../../../../utils/utils";

interface BatchDrawerProps {
  disclosureHost: DisclosureHost;
  setSelectedTaskModel?: (model: string[]) => void;
  title?: string;
  onResolve?: () => void;
}

type FormikValues = {
  resolvedDate: dayjs.Dayjs;
  note: string;
};

const validationSchema = Yup.object().shape({
  resolvedDate: Yup.date().required("Date is required"),
  note: Yup.string(),
});

const ResolvePatientTask = ({
  disclosureHost,
  setSelectedTaskModel,
  title = "Resolve Task",
  onResolve,
}: BatchDrawerProps) => {
  const { mutateAsync: batchResolved } = usePatientResolveTask();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const handleSubmit = async (values: FormikValues) => {
    showToastOnApiCall({
      apiCall: batchResolved({
        bulkResolved: {
          taskId: disclosureHost.data
            ? (disclosureHost.data as string[])
            : undefined,
          resolvedDate: values.resolvedDate?.toISOString() || "",
          description: values.note,
        },
        xTENANTID: getXtenantId(),
      }),
      showSnackbar,
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["collaboration-task-list"],
        });
        onResolve?.();
        setSelectedTaskModel?.([]);
        handleClose();
      },
    });
  };

  const handleClose = () => {
    formik.resetForm();
    disclosureHost.close();
  };

  const formik = useFormik<FormikValues>({
    initialValues: {
      resolvedDate: dayjs(new Date()),
      note: "",
    },
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <BatchResolveForm
      formik={formik}
      disclosureHost={disclosureHost}
      title={title}
      handleClose={handleClose}
    />
  );
};

export default ResolvePatientTask;
